import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Loader2, MapPin, Camera, Video, Mic, X, CheckCircle2, Send, AlertTriangle, Phone, Square, Play, Pause } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable practice creation form. Used by:
 * - Client (anonymous, with consultant_slug + initial profile from clientStore)
 * - Consultant (authenticated, types in client data directly)
 *
 * Props:
 *  - initialClient: { first_name, last_name, email, phone, vehicle_plate } or null
 *  - consultantSlug: required for anonymous client flow; null/undefined for consultant
 *  - consultant: consultant info (for quick-call buttons)
 *  - mode: "client" | "consultant"
 *  - requestSignature: bool — for consultant mode, default false
 *  - onSubmitted: (responseData) => void
 *  - showClientFields: bool — for consultant, show editable client form
 */
export default function PracticeForm({
  initialClient = null,
  consultantSlug = null,
  consultant = null,
  mode = "client",
  requestSignature = true,
  onSubmitted,
  showClientFields = false,
  allowedMediaTypes = ["photo", "video", "audio"],
  showDescription = true,
}) {
  const [client, setClient] = useState(initialClient || {
    first_name: "", last_name: "", email: "", phone: "", vehicle_plate: ""
  });
  const [description, setDescription] = useState("");
  const [hasInjuries, setHasInjuries] = useState(false);
  const [art9Consent, setArt9Consent] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [location, setLocation] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });
  const [requestSig, setRequestSig] = useState(requestSignature);

  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Audio recording
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordTimerRef = useRef(null);

  useEffect(() => {
    requestGps();
    return () => {
      // cleanup blob URLs
      [...photos, ...videos, ...audios].forEach((m) => {
        if (m.preview) URL.revokeObjectURL(m.preview);
      });
    };
    // eslint-disable-next-line
  }, []);

  const requestGps = () => {
    setGpsStatus("loading");
    if (!navigator.geolocation) {
      setGpsStatus("error");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setGpsStatus("ok");
      },
      () => setGpsStatus("error"),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
    );
  };

  const addFiles = (files, type) => {
    const arr = Array.from(files);
    const setters = { photo: setPhotos, video: setVideos, audio: setAudios };
    const getters = { photo: photos, video: videos, audio: audios };
    const limits = { photo: 15, video: 2, audio: 3 };
    const maxSize = { photo: 25, video: 100, audio: 25 };
    const accept = { photo: "image/", video: "video/", audio: "audio/" };

    const cur = getters[type];
    const setter = setters[type];

    if (cur.length + arr.length > limits[type]) {
      toast.error(`Massimo ${limits[type]} ${type === "photo" ? "foto" : type === "video" ? "video" : "audio"}`);
      return;
    }
    const valid = arr.filter((f) => {
      if (!f.type.startsWith(accept[type])) {
        toast.error(`File ${f.name} non valido`);
        return false;
      }
      if (f.size > maxSize[type] * 1024 * 1024) {
        toast.error(`${f.name} troppo grande (max ${maxSize[type]}MB)`);
        return false;
      }
      return true;
    });
    const newItems = valid.map((f) => ({ file: f, preview: URL.createObjectURL(f), name: f.name, size: f.size }));
    setter([...cur, ...newItems]);
  };

  const removeMedia = (idx, type) => {
    const setters = { photo: setPhotos, video: setVideos, audio: setAudios };
    const getters = { photo: photos, video: videos, audio: audios };
    const cur = getters[type];
    if (cur[idx]) URL.revokeObjectURL(cur[idx].preview);
    setters[type](cur.filter((_, i) => i !== idx));
  };

  // ----- Audio recording with MediaRecorder -----
  const startRecording = async () => {
    if (audios.length >= 3) {
      toast.error("Massimo 3 audio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], `registrazione-${Date.now()}.webm`, { type: blob.type });
        const preview = URL.createObjectURL(blob);
        setAudios((prev) => [...prev, { file, preview, name: file.name, size: file.size, duration: recordingTime }]);
        stream.getTracks().forEach((t) => t.stop());
        setRecordingTime(0);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecordingTime(0);
      recordTimerRef.current = setInterval(() => {
        setRecordingTime((t) => {
          if (t >= 120) {
            stopRecording();
            return t;
          }
          return t + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microfono non accessibile");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    setRecording(false);
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2, "0")}`;

  const totalMedia = photos.length + videos.length + audios.length;

  const submit = async () => {
    // Validate client fields when consultant entering manually
    if (showClientFields) {
      if (!client.first_name || !client.last_name || !client.email || !client.phone || !client.vehicle_plate) {
        toast.error("Compila tutti i dati cliente");
        return;
      }
    }
    // Art. 9 GDPR: explicit consent required when injuries are reported
    if (hasInjuries && !art9Consent) {
      toast.error("Hai segnalato la presenza di feriti: il consenso esplicito al trattamento dei dati di salute (art. 9 GDPR) è obbligatorio per poter inviare la pratica.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        phone: client.phone,
        vehicle_plate: client.vehicle_plate,
        description,
        latitude: location?.latitude,
        longitude: location?.longitude,
        gdpr_consent: true,
        has_injuries: hasInjuries,
        art9_consent: hasInjuries ? art9Consent : false,
      };
      if (consultantSlug) payload.consultant_slug = consultantSlug;
      if (mode === "consultant") payload.request_signature = requestSig;

      const { data } = await api.post("/practices", payload);

      // Upload media with progress
      const all = [
        ...photos.map((m) => ({ ...m, type: "photo" })),
        ...videos.map((m) => ({ ...m, type: "video" })),
        ...audios.map((m) => ({ ...m, type: "audio" })),
      ];
      for (let i = 0; i < all.length; i++) {
        const m = all[i];
        setProgress({ current: i + 1, total: all.length, label: m.type });
        const fd = new FormData();
        fd.append("file", m.file);
        fd.append("media_type", m.type);
        try {
          await api.post(`/practices/${data.id}/media`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
          });
        } catch (e) {
          console.error("Media upload failed", e);
          toast.error(`Errore upload ${m.type} ${i + 1}`);
        }
      }
      setProgress({ current: 0, total: 0, label: "" });
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Client data (for consultant mode) */}
      {showClientFields && (
        <div className="card-gs !p-4">
          <h3 className="font-bold text-[#1E293B] mb-3">Dati cliente</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-gs">Nome</label>
              <input data-testid="cform-firstname" required type="text" value={client.first_name}
                onChange={(e) => setClient({ ...client, first_name: e.target.value })}
                className="input-gs" />
            </div>
            <div>
              <label className="label-gs">Cognome</label>
              <input data-testid="cform-lastname" required type="text" value={client.last_name}
                onChange={(e) => setClient({ ...client, last_name: e.target.value })}
                className="input-gs" />
            </div>
            <div>
              <label className="label-gs">Email</label>
              <input data-testid="cform-email" required type="email" value={client.email}
                onChange={(e) => setClient({ ...client, email: e.target.value })}
                className="input-gs" />
            </div>
            <div>
              <label className="label-gs">Telefono</label>
              <input data-testid="cform-phone" required type="tel" value={client.phone}
                onChange={(e) => setClient({ ...client, phone: e.target.value })}
                className="input-gs" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-gs">Targa veicolo</label>
              <input data-testid="cform-plate" required type="text" value={client.vehicle_plate}
                onChange={(e) => setClient({ ...client, vehicle_plate: e.target.value.toUpperCase() })}
                className="input-gs uppercase" maxLength={10} placeholder="AB123CD" />
            </div>
          </div>
        </div>
      )}

      {/* GPS */}
      <div className="card-gs !p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-[#2456C6]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[#1E293B] mb-1">Posizione GPS</div>
            {gpsStatus === "loading" && (
              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-500">Acquisizione in corso…</div>
                <button onClick={() => setGpsStatus("error")} data-testid="gps-skip-button"
                  className="text-xs text-slate-400 hover:text-[#2456C6] underline">salta</button>
              </div>
            )}
            {gpsStatus === "ok" && (
              <div className="text-sm text-green-700 font-bold flex items-center gap-1 flex-wrap">
                <CheckCircle2 className="w-4 h-4" /> Posizione acquisita
                <span className="text-xs text-slate-500 ml-1 font-normal">{location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}</span>
              </div>
            )}
            {gpsStatus === "error" && (
              <div className="space-y-1">
                <div className="text-sm text-slate-500">Posizione non disponibile.</div>
                <button onClick={requestGps} data-testid="gps-retry-button"
                  className="text-sm font-bold text-[#F77F2A] hover:underline">Riprova</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photos */}
      <div className="card-gs !p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
            <Camera className="w-5 h-5 text-[#F77F2A]" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#1E293B]">Fotografie</div>
            <div className="text-xs text-slate-500">Max 15 · qualità originale (no compressione)</div>
          </div>
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square">
                <img src={p.preview} alt="" className="w-full h-full object-cover rounded-lg" />
                <button onClick={() => removeMedia(i, "photo")} data-testid={`remove-photo-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple hidden
          data-testid="photo-input"
          onChange={(e) => { addFiles(e.target.files, "photo"); e.target.value = ""; }} />
        {photos.length < 15 && (
          <button onClick={() => photoRef.current?.click()} data-testid="add-photo-button"
            className="w-full border-2 border-dashed border-slate-300 hover:border-[#F77F2A] rounded-xl py-3 text-sm font-bold text-slate-600 transition-colors">
            + Aggiungi foto ({photos.length}/15)
          </button>
        )}
      </div>

      {/* Videos */}
      {allowedMediaTypes.includes("video") && (
      <div className="card-gs !p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <Video className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#1E293B]">Video</div>
            <div className="text-xs text-slate-500">Max 2 · qualità originale (no compressione, max 100MB cad)</div>
          </div>
        </div>
        {videos.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {videos.map((v, i) => (
              <div key={i} className="relative">
                <video src={v.preview} controls className="w-full rounded-lg bg-black" />
                <button onClick={() => removeMedia(i, "video")} data-testid={`remove-video-${i}`}
                  className="absolute top-1 right-1 w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center z-10">
                  <X className="w-4 h-4" />
                </button>
                <div className="text-xs text-slate-500 mt-1 truncate">{v.name} · {(v.size/1024/1024).toFixed(1)}MB</div>
              </div>
            ))}
          </div>
        )}
        <input ref={videoRef} type="file" accept="video/*" capture="environment" hidden
          data-testid="video-input"
          onChange={(e) => { addFiles(e.target.files, "video"); e.target.value = ""; }} />
        {videos.length < 2 && (
          <button onClick={() => videoRef.current?.click()} data-testid="add-video-button"
            className="w-full border-2 border-dashed border-slate-300 hover:border-purple-500 rounded-xl py-3 text-sm font-bold text-slate-600 transition-colors">
            + Registra/Carica video ({videos.length}/2)
          </button>
        )}
      </div>
      )}

      {/* Audio */}
      {allowedMediaTypes.includes("audio") && (
      <div className="card-gs !p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
            <Mic className="w-5 h-5 text-green-700" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#1E293B]">Audio / Memo vocali</div>
            <div className="text-xs text-slate-500">Max 3 · qualità originale · per testimoni o note rapide</div>
          </div>
        </div>
        {audios.length > 0 && (
          <div className="space-y-2 mb-3">
            {audios.map((a, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <audio src={a.preview} controls className="flex-1 h-9" />
                <button onClick={() => removeMedia(i, "audio")} data-testid={`remove-audio-${i}`}
                  className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {audios.length < 3 && (
          <div className="grid grid-cols-2 gap-2">
            {!recording ? (
              <>
                <button onClick={startRecording} data-testid="record-audio-button"
                  className="border-2 border-dashed border-slate-300 hover:border-green-600 rounded-xl py-3 text-sm font-bold text-slate-600 flex items-center justify-center gap-2">
                  <Mic className="w-4 h-4" /> Registra ({audios.length}/3)
                </button>
                <button onClick={() => audioRef.current?.click()} data-testid="upload-audio-button"
                  className="border-2 border-dashed border-slate-300 hover:border-green-600 rounded-xl py-3 text-sm font-bold text-slate-600">
                  Carica file
                </button>
              </>
            ) : (
              <button onClick={stopRecording} data-testid="stop-record-button"
                className="col-span-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl py-3 flex items-center justify-center gap-2 animate-pulse">
                <Square className="w-4 h-4" /> Stop registrazione · {formatTime(recordingTime)} / 2:00
              </button>
            )}
          </div>
        )}
        <input ref={audioRef} type="file" accept="audio/*" hidden
          data-testid="audio-input"
          onChange={(e) => { addFiles(e.target.files, "audio"); e.target.value = ""; }} />
      </div>
      )}

      {/* Description */}
      {showDescription && (
      <div className="card-gs !p-4">
        <div className="font-bold text-[#1E293B] mb-2">Descrizione</div>
        <textarea data-testid="description-input" value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Es: tamponamento all'incrocio di via Roma, controparte targa XY123ZW"
          className="input-gs resize-none" rows={4} />
      </div>
      )}

      {/* Injuries (art. 9 GDPR) — health-category data require EXPLICIT consent */}
      <div className="card-gs !p-4" data-testid="injuries-block">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={hasInjuries}
            onChange={(e) => { setHasInjuries(e.target.checked); if (!e.target.checked) setArt9Consent(false); }}
            data-testid="has-injuries-checkbox"
            className="mt-1 w-5 h-5 accent-[#E11D48]" />
          <div>
            <div className="font-bold text-[#1E293B] text-sm">Nel sinistro sono presenti feriti o lesioni</div>
            <div className="text-xs text-slate-500">Spunta solo se ci sono persone che hanno riportato lesioni fisiche.</div>
          </div>
        </label>

        {hasInjuries && (
          <div className="mt-3 p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid="art9-consent-block">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={art9Consent}
                onChange={(e) => setArt9Consent(e.target.checked)}
                data-testid="art9-consent-checkbox"
                className="mt-1 w-5 h-5 accent-[#E11D48]" />
              <div className="text-xs text-slate-700 leading-relaxed">
                <b>Consenso esplicito al trattamento di dati relativi alla salute (art. 9 GDPR)</b><br />
                Acconsento espressamente al trattamento dei dati relativi alla mia salute (lesioni, ferite, esito accertamenti medici) comunicati nella presente pratica di sinistro, ai sensi dell&apos;art. 9, par. 2, lett. a) GDPR. Posso revocare il consenso in qualsiasi momento contattando il Titolare ai recapiti indicati nella <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline font-bold">Privacy Policy</a>.
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Consultant-only signature toggle */}
      {mode === "consultant" && (
        <div className="card-gs !p-4 bg-amber-50 border-amber-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={requestSig}
              onChange={(e) => setRequestSig(e.target.checked)}
              data-testid="request-signature-checkbox"
              className="mt-1 w-5 h-5 accent-[#F77F2A]" />
            <div>
              <div className="font-bold text-[#1E293B] text-sm">Richiedi firma OTP al cliente via email</div>
              <div className="text-xs text-slate-600">Se attivo, il cliente riceverà un OTP a 6 cifre per firmare digitalmente la pratica (FES eIDAS). Puoi richiederla anche più tardi dal dettaglio pratica.</div>
            </div>
          </label>
        </div>
      )}

      {/* Quick calls during emergency (only for client mode) */}
      {mode === "client" && consultant && (
        <div className="grid grid-cols-2 gap-2">
          <a href="tel:112" data-testid="report-call-112" className="btn-gs-emergency !min-h-[60px] !text-sm">
            <AlertTriangle className="w-5 h-5" /> 112
          </a>
          {consultant?.phone && (
            <a href={`tel:${consultant.phone}`} data-testid="report-call-consultant"
              className="bg-[#2456C6] hover:bg-[#1D449F] text-white font-bold rounded-xl flex items-center justify-center gap-2 min-h-[60px] active:scale-[0.97] transition">
              <Phone className="w-5 h-5" /> Consulente
            </a>
          )}
        </div>
      )}

      {/* Sticky-ish submit */}
      <div className="sticky bottom-2 z-10">
        <button onClick={submit} disabled={submitting} data-testid="submit-report-button"
          className="btn-gs-primary w-full !text-lg disabled:opacity-60 shadow-lg">
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {progress.total > 0
                ? `Upload ${progress.label} ${progress.current}/${progress.total}…`
                : "Invio in corso…"}
            </>
          ) : (
            <><Send className="w-5 h-5" /> {mode === "consultant" ? "CREA PRATICA" : "INVIA SEGNALAZIONE"}</>
          )}
        </button>
        {totalMedia > 0 && !submitting && (
          <div className="text-center text-xs text-slate-500 mt-2">
            {photos.length} foto · {videos.length} video · {audios.length} audio
          </div>
        )}
      </div>
    </div>
  );
}
